//#region Imports

import {
  CdkDrag,
  CdkDragDrop,
  CdkDragPlaceholder,
  CdkDragPreview,
  CdkDragSortEvent,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { NgClass } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DoCheck,
  EventEmitter,
  HostListener,
  Input,
  Output,
  inject,
} from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';
import { ContentType } from '../../models/enums/content-type.enum';
import { hasVisibleRichTextContent } from '../../helpers/rich-text.helper';
import { IconComponent } from '../icon/icon.component';
import { SortableCardComponent } from '../sortable-card/sortable-card.component';
import {
  ClassForm,
  ClassFormValue,
  EvaluationForm,
  ModuleForm,
} from '../../models/interfaces/forms/course-form.interface';

//#endregion

export interface SelectedCourseCard {
  type: 'content' | 'evaluation';
  id: string;
}

@Component({
  selector: 'l-course-content-step',
  standalone: true,
  templateUrl: './course-content-step.component.html',
  imports: [
    CdkDrag,
    CdkDragPlaceholder,
    CdkDragPreview,
    CdkDropList,
    NgClass,
    ReactiveFormsModule,
    IconComponent,
    SortableCardComponent,
    TooltipModule,
  ],
})
export class CourseContentStepComponent implements DoCheck {

  //#region Inputs

  @Input({ required: true })
  public modulesForm!: FormArray<FormGroup<ModuleForm>>;

  @Input()
  public selectedCard: SelectedCourseCard | null = null;

  //#endregion

  //#region Outputs

  @Output()
  public readonly selectedContentCardChange = new EventEmitter<SelectedCourseCard | null>();

  @Output()
  public readonly modulesChange = new EventEmitter<void>();

  @Output()
  public readonly addClass = new EventEmitter<string>();

  @Output()
  public readonly addEvaluation = new EventEmitter<string>();

  //#endregion

  //#region Public Properties

  public expandedModuleId: string | null = null;

  public draggingContentIdByModule: Record<string, string | null> = {};

  public draggingEvaluationIdByModule: Record<string, string | null> = {};

  //#endregion

  //#region Private Properties

  private previewOrderByModule: Record<string, string[]> = {};

  private previewEvaluationOrderByModule: Record<string, string[]> = {};

  private lastModulesLength = 0;

  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  private readonly committedDragModuleIds = new Set<string>();

  private readonly committedEvaluationDragModuleIds = new Set<string>();

  //#endregion

  //#region Lifecycle Hooks

  public ngDoCheck(): void {
    if (this.modulesForm.length !== this.lastModulesLength) {
      this.lastModulesLength = this.modulesForm.length;
      this.syncExpandedModule();
    }
  }

  //#endregion

  //#region Public Methods

  public isExpanded(moduleId: string): boolean {
    return this.expandedModuleId === moduleId;
  }

  public toggleModule(moduleId: string): void {
    this.expandedModuleId = this.isExpanded(moduleId) ? null : moduleId;
  }

  public getModuleClasses(moduleForm: FormGroup<ModuleForm>): FormGroup<ClassForm>[] {
    return moduleForm.controls.classes.controls;
  }

  public getModuleEvaluations(moduleForm: FormGroup<ModuleForm>): FormGroup<EvaluationForm>[] {
    return moduleForm.controls.evaluations.controls;
  }

  public onAddContent(moduleForm: FormGroup<ModuleForm>): void {
    this.addClass.emit(moduleForm.controls.id.value);
    this.expandedModuleId = moduleForm.controls.id.value;
    this.updateSelectedCard(null);
  }

  public onAddEvaluation(moduleForm: FormGroup<ModuleForm>): void {
    this.addEvaluation.emit(moduleForm.controls.id.value);
    this.expandedModuleId = moduleForm.controls.id.value;
  }

  public setActiveContentCard(cardId: string): void {
    this.updateSelectedCard({ type: 'content', id: cardId });
  }

  public setActiveEvaluationCard(cardId: string): void {
    this.updateSelectedCard({ type: 'evaluation', id: cardId });
  }

  public isContentCardActive(cardId: string): boolean {
    return this.selectedCard?.type === 'content' && this.selectedCard.id === cardId;
  }

  public isEvaluationCardActive(cardId: string): boolean {
    return this.selectedCard?.type === 'evaluation' && this.selectedCard.id === cardId;
  }

  public isDraggingContent(moduleId: string, cardId: string): boolean {
    return this.draggingContentIdByModule[moduleId] === cardId;
  }

  public isDraggingEvaluation(moduleId: string, cardId: string): boolean {
    return this.draggingEvaluationIdByModule[moduleId] === cardId;
  }

  public getContentDisplayIndex(moduleId: string, cardId: string): number {
    const moduleForm = this.findModuleForm(moduleId);
    const previewOrder = this.previewOrderByModule[moduleId];
    const order = previewOrder?.length
      ? previewOrder
      : (moduleForm?.controls.classes.controls.map((item) => item.controls.id.value) ?? []);
    const index = order.indexOf(cardId);
    return index === -1 ? 1 : index + 1;
  }

  public getEvaluationDisplayIndex(moduleId: string, cardId: string): number {
    const moduleForm = this.findModuleForm(moduleId);
    const previewOrder = this.previewEvaluationOrderByModule[moduleId];
    const order = previewOrder?.length
      ? previewOrder
      : (moduleForm?.controls.evaluations.controls.map((item) => item.controls.id.value) ?? []);
    const index = order.indexOf(cardId);
    return index === -1 ? 1 : index + 1;
  }

  public onDeleteContent(moduleForm: FormGroup<ModuleForm>, cardId: string): void {
    const classIndex = moduleForm.controls.classes.controls.findIndex((item) => item.controls.id.value === cardId);

    if (classIndex >= 0) {
      moduleForm.controls.classes.removeAt(classIndex);
      this.reindexClasses(moduleForm);
      this.modulesChange.emit();
    }

    if (this.selectedCard?.type === 'content' && this.selectedCard.id === cardId) {
      this.updateSelectedCard(null);
    }
  }

  public onDeleteEvaluation(moduleForm: FormGroup<ModuleForm>, cardId: string): void {
    const evaluationIndex = moduleForm.controls.evaluations.controls.findIndex((item) => item.controls.id.value === cardId);

    if (evaluationIndex >= 0) {
      moduleForm.controls.evaluations.removeAt(evaluationIndex);
      this.reindexEvaluations(moduleForm);
      this.modulesChange.emit();
    }

    if (this.selectedCard?.type === 'evaluation' && this.selectedCard.id === cardId) {
      this.updateSelectedCard(null);
    }
  }

  public onDragStartedForContent(moduleId: string, cardId: string): void {
    const moduleForm = this.findModuleForm(moduleId);

    this.draggingContentIdByModule = {
      ...this.draggingContentIdByModule,
      [moduleId]: cardId,
    };
    this.previewOrderByModule = {
      ...this.previewOrderByModule,
      [moduleId]: moduleForm?.controls.classes.controls.map((item) => item.controls.id.value) ?? [],
    };
    this.committedDragModuleIds.delete(moduleId);
  }

  public onContentDragEnded(moduleId: string): void {
    if (!this.committedDragModuleIds.has(moduleId)) {
      this.previewOrderByModule = { ...this.previewOrderByModule, [moduleId]: [] };
      this.draggingContentIdByModule = { ...this.draggingContentIdByModule, [moduleId]: null };
    }
    this.committedDragModuleIds.delete(moduleId);
  }

  public onContentListSorted(moduleId: string, event: CdkDragSortEvent): void {
    const previewOrder = this.previewOrderByModule[moduleId];
    if (!previewOrder?.length) {
      return;
    }
    moveItemInArray(previewOrder, event.previousIndex, event.currentIndex);
    this.changeDetectorRef.markForCheck();
  }

  public onContentReordered(moduleForm: FormGroup<ModuleForm>, event: CdkDragDrop<FormGroup<ClassForm>[]>): void {
    const moduleId = moduleForm.controls.id.value;

    if (event.previousIndex !== event.currentIndex) {
      const reordered = [...moduleForm.controls.classes.controls];
      moveItemInArray(reordered, event.previousIndex, event.currentIndex);
      moduleForm.controls.classes.clear();
      reordered.forEach((courseClass, order) => {
        courseClass.controls.order.setValue(order);
        moduleForm.controls.classes.push(courseClass);
      });
      this.modulesChange.emit();
    }

    this.committedDragModuleIds.add(moduleId);
    this.previewOrderByModule = { ...this.previewOrderByModule, [moduleId]: [] };
    this.draggingContentIdByModule = { ...this.draggingContentIdByModule, [moduleId]: null };
  }

  public onDragStartedForEvaluation(moduleId: string, cardId: string): void {
    const moduleForm = this.findModuleForm(moduleId);

    this.draggingEvaluationIdByModule = {
      ...this.draggingEvaluationIdByModule,
      [moduleId]: cardId,
    };
    this.previewEvaluationOrderByModule = {
      ...this.previewEvaluationOrderByModule,
      [moduleId]: moduleForm?.controls.evaluations.controls.map((item) => item.controls.id.value) ?? [],
    };
    this.committedEvaluationDragModuleIds.delete(moduleId);
  }

  public onEvaluationDragEnded(moduleId: string): void {
    if (!this.committedEvaluationDragModuleIds.has(moduleId)) {
      this.previewEvaluationOrderByModule = { ...this.previewEvaluationOrderByModule, [moduleId]: [] };
      this.draggingEvaluationIdByModule = { ...this.draggingEvaluationIdByModule, [moduleId]: null };
    }
    this.committedEvaluationDragModuleIds.delete(moduleId);
  }

  public onEvaluationListSorted(moduleId: string, event: CdkDragSortEvent): void {
    const previewOrder = this.previewEvaluationOrderByModule[moduleId];
    if (!previewOrder?.length) {
      return;
    }
    moveItemInArray(previewOrder, event.previousIndex, event.currentIndex);
    this.changeDetectorRef.markForCheck();
  }

  public onEvaluationReordered(
    moduleForm: FormGroup<ModuleForm>,
    event: CdkDragDrop<FormGroup<EvaluationForm>[]>,
  ): void {
    const moduleId = moduleForm.controls.id.value;

    if (event.previousIndex !== event.currentIndex) {
      const reordered = [...moduleForm.controls.evaluations.controls];
      moveItemInArray(reordered, event.previousIndex, event.currentIndex);
      moduleForm.controls.evaluations.clear();
      reordered.forEach((evaluation, order) => {
        evaluation.controls.order.setValue(order);
        moduleForm.controls.evaluations.push(evaluation);
      });
      this.modulesChange.emit();
    }

    this.committedEvaluationDragModuleIds.add(moduleId);
    this.previewEvaluationOrderByModule = { ...this.previewEvaluationOrderByModule, [moduleId]: [] };
    this.draggingEvaluationIdByModule = { ...this.draggingEvaluationIdByModule, [moduleId]: null };
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    if (!this.selectedCard) {
      return;
    }

    const cardSelector = this.selectedCard.type === 'content'
      ? `[data-content-card-id="${this.selectedCard.id}"]`
      : `[data-evaluation-card-id="${this.selectedCard.id}"]`;

    if (this.isClickInside(cardSelector, event) || this.isClickInside('[data-course-content-panel]', event)) {
      return;
    }

    this.updateSelectedCard(null);
  }

  public getModuleTitle(moduleForm: FormGroup<ModuleForm>): string {
    const name = moduleForm.controls.name.value.trim();
    return name || 'Sem título';
  }

  public isModuleContentComplete(moduleId: string): boolean {
    const moduleForm = this.findModuleForm(moduleId);
    const classes = moduleForm?.controls.classes.controls ?? [];

    if (classes.length === 0) {
      return false;
    }

    return classes.every((courseClass) => this.isClassFilled(courseClass.getRawValue()));
  }

  public getModuleContentStatusTooltip(moduleId: string): string {
    const moduleForm = this.findModuleForm(moduleId);

    if ((moduleForm?.controls.classes.length ?? 0) === 0) {
      return 'O módulo não possui conteúdos adicionados';
    }
    return 'Existem conteúdos que não possuem dados adicionados';
  }

  //#endregion

  //#region Private Methods

  private isClassFilled(classValue: ClassFormValue): boolean {
    if (!classValue.type) {
      return false;
    }

    switch (classValue.type) {
      case ContentType.VIDEO:
        return !!(
          classValue.videoFileName.trim()
          || classValue.videoTitle.trim()
          || classValue.videoThumbnailUrl.trim()
        );
      case ContentType.RICH_TEXT:
        return hasVisibleRichTextContent(classValue.editorContent);
      case ContentType.YOUTUBE:
        return classValue.youtubeUrl.trim().length > 0;
      case ContentType.VIMEO:
        return classValue.vimeoUrl.trim().length > 0;
      case ContentType.HTML:
        return classValue.webPageFileName.trim().length > 0;
      default:
        return false;
    }
  }

  private reindexClasses(moduleForm: FormGroup<ModuleForm>): void {
    moduleForm.controls.classes.controls.forEach((courseClass, order) => {
      courseClass.controls.order.setValue(order);
    });
  }

  private reindexEvaluations(moduleForm: FormGroup<ModuleForm>): void {
    moduleForm.controls.evaluations.controls.forEach((evaluation, order) => {
      evaluation.controls.order.setValue(order);
    });
  }

  private isClickInside(selector: string, event: MouseEvent): boolean {
    return event.composedPath().some((node) => node instanceof Element && node.matches(selector));
  }

  private findModuleForm(moduleId: string): FormGroup<ModuleForm> | undefined {
    return this.modulesForm.controls.find((item) => item.controls.id.value === moduleId);
  }

  private syncExpandedModule(): void {
    if (this.modulesForm.length === 0) {
      this.expandedModuleId = null;
      this.updateSelectedCard(null);
      return;
    }

    const availableModuleIds = new Set(this.modulesForm.controls.map((item) => item.controls.id.value));
    this.draggingContentIdByModule = Object.fromEntries(
      Object.entries(this.draggingContentIdByModule).filter(([moduleId]) => availableModuleIds.has(moduleId)),
    );
    this.draggingEvaluationIdByModule = Object.fromEntries(
      Object.entries(this.draggingEvaluationIdByModule).filter(([moduleId]) => availableModuleIds.has(moduleId)),
    );
    this.previewOrderByModule = Object.fromEntries(
      Object.entries(this.previewOrderByModule).filter(([moduleId]) => availableModuleIds.has(moduleId)),
    );
    this.previewEvaluationOrderByModule = Object.fromEntries(
      Object.entries(this.previewEvaluationOrderByModule).filter(([moduleId]) => availableModuleIds.has(moduleId)),
    );

    const expandedStillExists = this.modulesForm.controls.some((item) => item.controls.id.value === this.expandedModuleId);
    if (!expandedStillExists) {
      this.expandedModuleId = this.modulesForm.controls[0].controls.id.value;
    }

    if (this.selectedCard) {
      const cardStillExists = this.selectedCard.type === 'content'
        ? this.modulesForm.controls.some((module) =>
          module.controls.classes.controls.some((item) => item.controls.id.value === this.selectedCard!.id),
        )
        : this.modulesForm.controls.some((module) =>
          module.controls.evaluations.controls.some((item) => item.controls.id.value === this.selectedCard!.id),
        );

      if (!cardStillExists) {
        this.updateSelectedCard(null);
      }
    }
  }

  private updateSelectedCard(card: SelectedCourseCard | null): void {
    this.selectedContentCardChange.emit(card);
  }

  //#endregion

}
